import { useEffect } from "react";
import { Link } from "react-router-dom";

const aboutHero =
  "https://readdy.ai/api/search-image?query=Stylized%20editorial%20illustration%20of%20a%20warm%20open%20doorway%20leading%20to%20a%20starry%20night%20landscape%2C%20warm%20amber%20and%20cream%20tones%2C%20inviting%20and%20sophisticated%20magazine%20art%2C%20no%20text&width=1600&height=700&seq=about-hero-001&orientation=landscape";

const principles = [
  { icon: "ri-eye-line", title: "实机体验", desc: "每一篇评测都来自编辑真实游玩体验，拒绝云评与恰饭吹捧。" },
  { icon: "ri-dashboard-3-line", title: "评分透明", desc: "围绕剧情、美术、演出、音乐与玩法五项加权，公开每一项得分。" },
  { icon: "ri-chat-smile-3-line", title: "社区共建", desc: "欢迎投稿、纠错与推荐遗珠，让榜单始终贴近真实口碑。" },
  { icon: "ri-shield-user-line", title: "理性倡导", desc: "理性消费、适度游玩，尊重年龄与地区合规要求。" },
];

const testimonials = [
  { name: "林晚", role: "入坑玩家", text: "跟着入门清单玩的第一部就是樱花便利店，从此打开了新世界。评测写得真诚，不踩雷。" },
  { name: "阿澈", role: "剧情党", text: "这里的评分很靠谱，尤其是神作盘点，几乎没有让我失望过。悬疑分类挖到好多遗珠。" },
  { name: "橘子汽水", role: "老玩家", text: "编辑的文字功底在线，能讲清楚好在哪、适合谁，比堆参数的老评测有温度多了。" },
];

export default function About() {
  useEffect(() => {
    document.title = "关于幻夜志 | 品牌介绍、发展理念与用户口碑";
  }, []);

  return (
    <>
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img src={aboutHero} alt="关于幻夜志背景" className="h-full w-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-background-50"></div>
        </div>
        <div className="relative mx-auto w-full max-w-7xl px-4 pb-16 pt-16 md:px-6 md:pb-20 md:pt-20">
          <nav aria-label="面包屑" className="text-xs text-background-100/80">
            <ol className="flex items-center gap-1.5">
              <li><Link to="/" className="hover:text-background-50">首页</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li aria-current="page" className="font-medium text-background-50">关于幻夜志</li>
            </ol>
          </nav>
          <h1 className="mt-5 max-w-2xl font-display text-3xl font-black leading-snug text-background-50 md:text-4xl">
            我们只收录值得一玩的作品
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-100/90 md:text-base">
            成人游戏市场庞大而混杂，踩雷的成本很高。幻夜志希望成为一座灯塔，
            用实机体验与真诚文字，帮每个玩家在正确的时间遇见正确的作品。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-16">
        <div className="grid gap-10 lg:grid-cols-2 lg:items-start">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-accent-600">Our Story</p>
            <h2 className="mt-2 font-display text-2xl font-bold text-foreground-950 md:text-3xl">品牌介绍</h2>
            <p className="mt-4 text-sm leading-loose text-foreground-700">
              「幻夜志」诞生于一群对叙事游戏近乎偏执的编辑之手。我们相信，好的成人游戏首先是好的故事，
              它值得被认真对待、被认真评价。于是我们开始亲手去玩、去记录、去打分，
              把值得一玩的作品从茫茫大海中捞出来，再讲清楚它好在哪里。
            </p>
            <p className="mt-3 text-sm leading-loose text-foreground-700">
              从口碑榜到神作盘点，从入门推荐到避坑指南，我们希望这里既是新手的向导，
              也是老玩家挖掘遗珠的宝库。每一篇文章、每一个评分，背后都是真实的游玩时间与思考。
            </p>

            <div className="mt-8 grid grid-cols-2 gap-4">
              {[
                { icon: "ri-gamepad-line", num: "128+", label: "实机评测" },
                { icon: "ri-star-fill", num: "8 类", label: "主题分类" },
                { icon: "ri-medal-line", num: "12 部", label: "神作盘点" },
                { icon: "ri-group-line", num: "30w+", label: "社区口碑" },
              ].map((s) => (
                <div key={s.label} className="rounded-xl border border-background-200/70 bg-background-100 p-5 text-center">
                  <i className={`${s.icon} text-xl text-primary-600`}></i>
                  <div className="mt-2 font-display text-2xl font-black text-foreground-950">{s.num}</div>
                  <div className="mt-1 text-xs text-foreground-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-background-200/70 bg-background-100 p-7">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-accent-600">Principles</p>
            <h2 className="mt-2 font-display text-xl font-bold text-foreground-950 md:text-2xl">内容原则</h2>
            <div className="mt-6 space-y-5">
              {principles.map((p) => (
                <div key={p.title} className="flex gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                    <i className={`${p.icon} text-xl`}></i>
                  </span>
                  <div>
                    <h3 className="font-display text-base font-bold text-foreground-950">{p.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100/70">
        <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-16">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-accent-600">Voices</p>
          <h2 className="mt-2 font-display text-2xl font-bold text-foreground-950 md:text-3xl">用户口碑</h2>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {testimonials.map((t) => (
              <figure key={t.name} className="flex flex-col rounded-2xl border border-background-200/70 bg-background-50 p-6">
                <div className="flex gap-0.5 text-primary-500">
                  {Array.from({ length: 5 }).map((_, i) => <i key={i} className="ri-star-fill text-sm"></i>)}
                </div>
                <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-foreground-700">「{t.text}」</blockquote>
                <figcaption className="mt-5 flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-100 font-display text-sm font-bold text-accent-700">
                    {t.name[0]}
                  </span>
                  <div>
                    <div className="text-sm font-medium text-foreground-950">{t.name}</div>
                    <div className="text-xs text-foreground-500">{t.role}</div>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-16">
        <div className="flex flex-col items-start justify-between gap-6 rounded-2xl bg-background-950 p-8 md:flex-row md:items-center md:p-10">
          <div>
            <h2 className="font-display text-2xl font-bold text-background-50">加入幻夜志</h2>
            <p className="mt-3 max-w-lg text-sm leading-relaxed text-background-100/80">
              无论是投稿新作、纠错反馈，还是推荐你心中的遗珠之作，我们都非常欢迎。
              一起让这份榜单更贴近真实口碑。
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <a href="mailto:hello@huanyezhi.example.com" className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600">
              <i className="ri-mail-line"></i> 给我们写信
            </a>
            <Link to="/ranking" className="inline-flex items-center gap-2 rounded-full bg-background-50 px-6 py-3 text-sm font-medium text-foreground-950 whitespace-nowrap transition-colors hover:bg-background-100">
              浏览排行榜
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}